import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, FolderOpen, Image, Layers
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const AdminCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: ''
  });

  const token = localStorage.getItem('adminToken');
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    // Apenas admin ou programador pode acessar
    if (userRole !== 'programmer' && userRole !== 'admin') {
      navigate('/admin');
      return;
    }
    fetchCategories();
    const interval = setInterval(() => {
      fetchCategories();
    }, 30000);
    return () => clearInterval(interval);
  }, [userRole, navigate, token]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      } else {
        await axios.post(`${API}/categories`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      }

      setIsDialogOpen(false);
      setEditingCategory(null);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Erro ao salvar categoria');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image: category.image || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      const detail = error?.response?.data?.detail;
      const count = error?.response?.data?.products_count;
      const msg = detail
        ? (typeof count === 'number'
          ? `${detail} (Produtos vinculados: ${count})`
          : detail)
        : 'Erro ao excluir categoria';
      alert(msg);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      image: ''
    });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const { darkMode, theme } = useAdminTheme();

  return (
    <AdminLayout title="Categorias" subtitle="Gerencie as categorias de produtos">
      {/* Header Actions */}
      <div data-tour="categories-header" className="flex items-center justify-between mb-6">
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Layers className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total de Categorias</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{categories.length}</p>
            </div>
          </CardContent>
        </Card>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingCategory(null); resetForm(); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" /> Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent className={`${theme.card} border ${theme.text}`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
              <DialogDescription className={theme.textMuted}>
                Preencha os dados da categoria
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className={theme.text}>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData, 
                      name,
                      slug: editingCategory ? formData.slug : generateSlug(name)
                    });
                  }}
                  className={theme.input}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>Slug (URL)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className={theme.input}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={theme.input}
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>URL da Imagem</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className={theme.input}
                  placeholder="https://..."
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingCategory ? 'Salvar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className={`h-12 w-12 ${theme.textMuted} mx-auto mb-4`} />
          <p className={theme.textMuted}>Nenhuma categoria cadastrada</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => (
            <Card key={category.id} className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <div className={`h-40 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} relative`}>
                {category.image ? (
                  <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className={`h-12 w-12 ${theme.textMuted}`} />
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className={`${theme.text} font-semibold text-lg`}>{category.name}</h3>
                <p className={`${theme.textMuted} text-sm mb-2`}>/{category.slug}</p>
                {category.description && (
                  <p className={`${theme.textMuted} text-sm line-clamp-2`}>{category.description}</p>
                )}
                <div className="flex space-x-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(category)} className="flex-1">
                    <Edit className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(category.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};
